import { useCallback, useRef, useState } from 'react';
import { seedFromHex } from '@/lib/polaroid-utils';

const DOODLE_COUNT = 11;

interface DateDoodleProps {
  /** A hex string used as the seed for deterministic doodle selection */
  seed: string;
  /** Size of the doodle in pixels */
  size?: number;
  className?: string;
  /**
   * When set, wraps the doodle in a span with this CSS class for a paper
   * background (e.g. 'paper-dot' or 'paper-dot-pastel'). The background
   * spins together with the doodle as one physical piece of paper.
   */
  paperClass?: string;
  /** Override the paper background color (takes precedence over paperClass's CSS background) */
  paperColor?: string;
  /** When false, disables the spin-on-click behavior and lets clicks pass through to the parent. Default true. */
  interactive?: boolean;
}

/**
 * Renders one of the 11 hand-drawn SVG doodles from /public/doodles/.
 * The doodle is selected deterministically based on the seed string.
 * Tapping/clicking triggers a playful spin animation.
 */
export function DateDoodle({ seed, size = 28, className = '', paperClass, paperColor, interactive = true }: DateDoodleProps) {
  // Pick a doodle (1-11) deterministically from the seed
  const doodleIndex = (seedFromHex(seed, 0, 4) % DOODLE_COUNT) + 1;

  // Slight random rotation for that hand-drawn feel
  const baseRotation = (seedFromHex(seed, 4, 8) % 30) - 15;

  const [spinning, setSpinning] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!interactive) return;
    e.stopPropagation();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setSpinning(false);
    requestAnimationFrame(() => {
      setSpinning(true);
      timeoutRef.current = setTimeout(() => setSpinning(false), 600);
    });
  }, [interactive]);

  const spinStyle: React.CSSProperties = {
    display: 'inline-block',
    transform: `rotate(${spinning ? baseRotation + 360 : baseRotation}deg)`,
    transition: spinning ? 'transform 600ms cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
    cursor: interactive ? 'pointer' : undefined,
    pointerEvents: interactive ? undefined : 'none',
  };

  // Render the doodle as a masked div so its color matches the theme
  // text color. The SVG (always black) is used as a mask-image, and
  // the element's background-color (the text color) shows through.
  const doodleSrc = `/doodles/${doodleIndex}.svg`;
  const img = (
    <div
      role="presentation"
      className={`select-none ${className}`}
      style={{
        width: size,
        height: size,
        opacity: 0.7,
        backgroundColor: 'rgb(var(--pol-text-color))',
        WebkitMaskImage: `url(${doodleSrc})`,
        WebkitMaskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskImage: `url(${doodleSrc})`,
        maskSize: 'contain',
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
      }}
    />
  );

  // When paperClass is set, the paper background wraps the doodle and the
  // whole unit spins together — like one piece of paper with a drawing on it.
  if (paperClass) {
    return (
      <span
        className={`${paperClass} inline-block`}
        style={paperColor ? { ...spinStyle, backgroundColor: paperColor } : spinStyle}
        onClick={handleClick}
      >
        {img}
      </span>
    );
  }

  // No paper background — spin the image directly
  return (
    <span style={spinStyle} onClick={handleClick}>
      {img}
    </span>
  );
}
